import React, { useEffect, useState, useRef, Children } from 'react';
import { motion, useInView } from 'framer-motion';
function Counter({
  from,
  to,
  duration = 2
}: {
  from: number;
  to: number;
  duration?: number;
}) {
  const [count, setCount] = useState(from);
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  useEffect(() => {
    if (isInView) {
      let startTime: number;
      const step = (timestamp: number) => {
        if (!startTime) startTime = timestamp;
        const progress = Math.min((timestamp - startTime) / (duration * 1000), 1);
        setCount(Math.floor(progress * (to - from) + from));
        if (progress < 1) {
          window.requestAnimationFrame(step);
        }
      };
      window.requestAnimationFrame(step);
    }
  }, [isInView, from, to, duration]);
  return <span ref={ref} data-id="element-48">{count}</span>;
}
export function AboutSection() {
  const textVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: 'easeOut'
      }
    }
  };
  return <section id="company" className="py-32 md:py-40 bg-brand-beige relative overflow-hidden" data-id="element-49">
      {/* Noise Overlay */}
      <div className="absolute inset-0 bg-noise pointer-events-none z-20 opacity-50" data-id="element-50"></div>

      {/* Watermark */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-[20vw] font-serif font-bold text-brand-red/[0.03] select-none pointer-events-none z-0 whitespace-nowrap" data-id="element-51">
        EST. 2009
      </div>

      {/* Animated Horizontal Line */}
      <motion.div className="absolute top-0 left-0 h-px bg-brand-terracotta/40 z-10" initial={{
      width: 0
    }} whileInView={{
      width: '100%'
    }} viewport={{
      once: true
    }} transition={{
      duration: 1.5,
      ease: 'easeInOut'
    }} data-id="element-52" />

      {/* Decorative elements */}
      <div className="absolute -left-20 top-20 w-64 h-64 border border-brand-terracotta/20 rounded-full z-0" data-id="element-53"></div>
      <div className="absolute -right-20 bottom-20 w-96 h-96 border border-brand-gold/20 rounded-full z-0" data-id="element-54"></div>

      <div className="max-w-6xl mx-auto px-6 md:px-12 relative z-10" data-id="element-55">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-16 items-start" data-id="element-56">
          <motion.div className="md:col-span-4" initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.8,
          ease: 'easeOut'
        }} data-id="element-57">
            <h2 className="text-5xl md:text-6xl font-serif text-brand-dark mb-8 leading-tight" data-id="element-58">
              Our <br data-id="element-59" />
              <span className="text-brand-red italic" data-id="element-60">Story</span>
            </h2>
            <motion.div className="w-24 h-1 bg-brand-terracotta mb-8" initial={{
            scaleX: 0
          }} whileInView={{
            scaleX: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8,
            delay: 0.4
          }} style={{
            originX: 0
          }} data-id="element-61" />
          </motion.div>

          <div className="md:col-span-8" data-id="element-62">
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            staggerChildren: 0.2
          }} className="space-y-8" data-id="element-63">
              <motion.p variants={textVariants} className="text-xl md:text-2xl text-brand-dark/90 font-light leading-relaxed" data-id="element-64">
                Founded in 2010, Jibek Jolu began as a humble family restaurant
                inspired by the rich flavors and time-honored traditions of
                Central Asia.
              </motion.p>
              <motion.p variants={textVariants} className="text-lg md:text-xl text-brand-dark/70 font-light leading-relaxed" data-id="element-65">
                Today, we have grown into a diverse hospitality group
                encompassing unique dining concepts, catering services, and
                premium event spaces. Despite our growth, our core philosophy
                remains unchanged: to provide honest food, exceptional service,
                and a welcoming atmosphere that feels like home.
              </motion.p>
            </motion.div>

            <motion.div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-8 pt-12 border-t border-brand-dark/10" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8,
            delay: 0.6
          }} data-id="element-66">
              {[{
              label: 'Concepts',
              value: 5,
              suffix: '+'
            }, {
              label: 'Locations',
              value: 12,
              suffix: ''
            }, {
              label: 'Team Members',
              value: 300,
              suffix: '+'
            }, {
              label: 'Years',
              value: 15,
              suffix: '+'
            }].map((stat, idx) => <div key={idx} className="border-l-2 border-brand-red/30 pl-6" data-id="element-67">
                  <div className="text-4xl md:text-5xl font-serif text-brand-dark mb-2 flex items-baseline" data-id="element-68">
                    <Counter from={0} to={stat.value} duration={2 + idx * 0.2} data-id="element-69" />
                    <span className="text-brand-terracotta ml-1" data-id="element-70">
                      {stat.suffix}
                    </span>
                  </div>
                  <div className="text-xs uppercase tracking-[0.2em] text-brand-dark/60 font-medium" data-id="element-71">
                    {stat.label}
                  </div>
                </div>)}
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}